import React, { useMemo } from "react";
import { Target, CheckCircle2, Circle } from "lucide-react";
import { cn } from "@/lib/utils";

interface TimelineItem {
  id: string;
  label: string;
  timeframe?: string;
}

interface TimelineVisualizationProps {
  items?: TimelineItem[];
  formValues?: Record<string, any>;
  timeframe?: string;
  className?: string;
}

export function TimelineVisualization({ 
  items = [], 
  formValues = {}, 
  timeframe = "Ziel",
  className 
}: TimelineVisualizationProps) {
  const timelineData = useMemo(() => {
    // Extract goals/timeline items from form values
    const goalItems: { label: string; completed: boolean; timeframe: string }[] = [];
    
    // Check for goal-related form values
    Object.entries(formValues).forEach(([key, value]) => {
      if (key.includes('ziel') || key.includes('goal') || key.includes('priorität')) {
        if (value && typeof value === 'string') {
          goalItems.push({
            label: value,
            completed: false,
            timeframe: 'Kurzfristig',
          });
        }
      }
    });

    // Use provided items or defaults
    if (goalItems.length === 0) {
      return [
        { label: 'Kurzfristige Ziele', completed: false, timeframe: '0-2 Jahre' },
        { label: 'Mittelfristige Ziele', completed: false, timeframe: '3-5 Jahre' },
        { label: 'Langfristige Ziele', completed: false, timeframe: '5+ Jahre' },
      ];
    }

    return goalItems;
  }, [items, formValues]);

  return (
    <div className={cn("space-y-6", className)}>
      {/* Header */}
      <div className="flex items-center gap-3 p-4 bg-primary/5 rounded-xl">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
          <Target className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h3 className="font-semibold">Ihre Ziele</h3>
          <p className="text-sm text-muted-foreground">Zeitliche Planung</p>
        </div>
      </div>

      {/* Timeline */}
      <div className="relative pl-8">
        {/* Vertical Line */}
        <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-primary/50 to-primary/20" />
        
        <div className="space-y-6">
          {timelineData.map((item, index) => (
            <div 
              key={index} 
              className="relative animate-in fade-in slide-in-from-left-2"
              style={{ animationDelay: `${index * 150}ms` }}
            >
              {/* Dot */}
              <div 
                className={cn(
                  "absolute -left-5 w-4 h-4 rounded-full border-2 border-background transition-all",
                  item.completed 
                    ? "bg-chart-5" 
                    : index === 0 
                      ? "bg-primary animate-pulse" 
                      : "bg-muted-foreground/30"
                )}
              />
              
              {/* Content Card */}
              <div className={cn(
                "p-4 rounded-lg transition-all",
                index === 0 
                  ? "bg-primary/10 border border-primary/20" 
                  : "bg-muted/50 hover:bg-muted"
              )}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground mb-1">
                      {item.timeframe}
                    </p>
                    <p className={cn(
                      "font-medium",
                      index === 0 && "text-primary"
                    )}>
                      {item.label}
                    </p>
                  </div>
                  {item.completed ? (
                    <CheckCircle2 className="h-5 w-5 text-chart-5 flex-shrink-0" />
                  ) : (
                    <Circle className="h-5 w-5 text-muted-foreground/30 flex-shrink-0" />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
